import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppWindow, Download, Github, QrCode, KeyRound, PlayCircle, Monitor, CheckCircle2 } from "lucide-react";

const API = "/api";

const steps = [
  { icon: KeyRound, title: "Crea un perfil por cuenta", text: "En Perfiles, crea un perfil tipo Chrome (RCS) por cada linea de Google Messages y copia su token." },
  { icon: AppWindow, title: "Agrega la cuenta en la app", text: "Pulsa \"+ Agregar cuenta\", pega la URL de este panel y el token. La app valida el token contra el servidor." },
  { icon: QrCode, title: "Escanea el QR", text: "En el telefono: Google Messages → tu foto → Vinculacion de dispositivos → Codigo QR. La sesion queda guardada en la app." },
  { icon: PlayCircle, title: "Listo, envia", text: "Las campanas asignadas a ese perfil se procesan solas con el mismo motor anti-ban de la extension. Puedes iniciar/detener por cuenta o todas a la vez." },
];

export default function DesktopApp() {
  const [version, setVersion] = useState(null);

  useEffect(() => {
    fetch(`${API}/desktop-app/version`).then((r) => r.json()).then(setVersion).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="desktop-app-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Monitor className="w-6 h-6 text-cyan-400" /> App de Escritorio (Windows)
        </h1>
        <p className="text-sm text-zinc-400 mt-1">
          Gestiona <span className="text-white font-medium">multiples cuentas de Google Messages</span> desde una sola ventana, sin Chrome ni extension.
          Cada cuenta corre en un navegador aislado dentro de la app y usa el mismo motor de envio que la extension RCS.
        </p>
        {version && (
          <p className="text-[11px] font-mono text-zinc-500 mt-2" data-testid="desktop-app-version">
            App v{version.version} · Motor de extension v{version.engine_version}
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-zinc-900/60 border border-cyan-500/20 rounded-xl p-5 space-y-3" data-testid="desktop-installer-card">
          <div className="flex items-center gap-2 text-cyan-300 font-semibold"><Github className="w-4 h-4" /> Instalador .exe (GitHub Actions)</div>
          <p className="text-xs text-zinc-400 leading-relaxed">
            El instalador se compila automaticamente cuando guardas el proyecto en GitHub (<span className="text-zinc-200">Save to GitHub</span>).
            El workflow <code className="text-cyan-400/80">.github/workflows/build-desktop.yml</code> genera y publica en
            <span className="text-zinc-200"> Releases → desktop-v{version?.version || "1.0.0"}</span>:
          </p>
          <ul className="text-xs text-zinc-300 space-y-1.5">
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> RCS-Sender-Desktop-Setup-{version?.version || "1.0.0"}.exe (instalador)</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> RCS-Sender-Desktop-Portable-{version?.version || "1.0.0"}.exe (sin instalacion)</li>
          </ul>
          <p className="text-[11px] text-zinc-500">Tras el primer push, abre la pestana <b>Actions</b> del repositorio y espera ~5 minutos.</p>
        </div>

        <div className="bg-zinc-900/60 border border-white/5 rounded-xl p-5 space-y-3" data-testid="desktop-source-card">
          <div className="flex items-center gap-2 text-zinc-200 font-semibold"><Download className="w-4 h-4" /> Codigo fuente (ZIP)</div>
          <p className="text-xs text-zinc-400 leading-relaxed">
            Para desarrolladores: descarga el proyecto Electron y compila en tu PC con Node.js 20
            (<code className="text-cyan-400/80">npm install && npm run dist</code>).
          </p>
          <a
            href={`${window.location.origin}${API}/desktop-app/download`}
            download
            data-testid="download-desktop-source-btn"
            className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Descargar codigo fuente
          </a>
        </div>
      </div>

      <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-5">
        <h2 className="text-base font-semibold text-white mb-4">Como vincular cuentas</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {steps.map((s, i) => (
            <div key={s.title} className="flex gap-3" data-testid={`desktop-step-${i + 1}`}>
              <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <s.icon className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{i + 1}. {s.title}</p>
                <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{s.text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/profiles" className="text-xs text-cyan-400 hover:text-cyan-300 underline underline-offset-2" data-testid="desktop-go-profiles-link">Ir a Perfiles para copiar tokens →</Link>
        </div>
      </div>

      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 text-xs text-amber-200/80 leading-relaxed" data-testid="desktop-notes">
        <b className="text-amber-300">Importante:</b> el telefono de cada cuenta debe permanecer encendido y con internet (Google Messages Web envia los RCS a traves del telefono).
        La app inicia con Windows en segundo plano y se minimiza a la bandeja del sistema; los perfiles conectados desde la app aparecen en Perfiles con la etiqueta <span className="font-mono text-cyan-300">Escritorio</span>.
      </div>
    </div>
  );
}
